// src/auth/AuthContext.jsx
//
// SINGLE source of truth for auth + role + tenant.
// On every auth state change:
//   1. If logged out -> clear state.
//   2. If logged in:
//      a. Check if email is a SuperAdmin (from .env).
//         -> SuperAdmin: role='SuperAdmin', tenantId=null, tenantName=null.
//      b. Otherwise, look up UserProfileMaster where email=user.email.
//         -> If found and status='active': set role/tenantId/tenantName from doc.
//         -> If not found or inactive: signOut + show alert.
//
// ANY component needing auth uses: const { currentUser, role, tenantId, tenantName, loading, logout } = useAuth();
//
// IMPORTANT: This file replaces the broken `./AuthContext` import that
// UserManagement.js and TenantManagement.js were using.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { auth, firestore } from '../components/firebase.config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
};

const getSuperAdminEmails = () =>
  [
    process.env.REACT_APP_ADMIN_EMAIL1,
    process.env.REACT_APP_ADMIN_EMAIL2,
    process.env.REACT_APP_ADMIN_EMAIL3,
  ]
    .filter(Boolean)
    .map((e) => e.toLowerCase().trim());

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [role, setRole] = useState(null); // 'SuperAdmin' | 'AppAdmin' | 'AppUser' | null
  const [tenantId, setTenantId] = useState(null);
  const [tenantName, setTenantName] = useState(null);
  const [profileDocId, setProfileDocId] = useState(null); // UserProfileMaster doc id (null for SuperAdmin)
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const [tenantBranding, setTenantBranding] = useState(null);
  const [tenantApplications, setTenantApplications] = useState({cbam: true, energyAudit: true,});
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const clearState = () => {
    setCurrentUser(null);
    setRole(null);
    setTenantId(null);
    setTenantName(null);
    setProfileDocId(null);
    setChatbotEnabled(false);
    setTenantBranding(null);

    setTenantApplications({
  cbam: true,
  energyAudit: true,
});
  };

  const logout = async () => {
  const tenantId = sessionStorage.getItem("activeTenantLoginId");

  try {
    await signOut(auth);
  } finally {
    clearState();

    sessionStorage.removeItem("activeTenantLoginId");

    window.location.href = tenantId
      ? `/t/${tenantId}`
      : "/login";
  }
};

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setAuthError(null);

      if (!user) {
        clearState();
        setLoading(false);
        return;
      }

      const email = (user.email || '').toLowerCase().trim();
      const superAdmins = getSuperAdminEmails();

      // 1. SuperAdmin path - .env only, NEVER touch Firestore.
      if (superAdmins.includes(email)) {
        setCurrentUser(user);
        setRole('SuperAdmin');
        setTenantId(null);
        setTenantName(null);
        setProfileDocId(null);
        setChatbotEnabled(true);
        setTenantBranding(null);
        setLoading(false);
        return;
      }

      // 2. Regular user - look up in UserProfileMaster by email.
      try {
        const profileQuery = query(
          collection(firestore, 'UserProfileMaster'),
          where('email', '==', email)
        );
        const snap = await getDocs(profileQuery);

        if (snap.empty) {
          // Not whitelisted - sign out immediately.
          await signOut(auth);
          clearState();
          setAuthError('Your account is not authorized. Contact your administrator.');
          setLoading(false);
          return;
        }

        const profileDoc = snap.docs[0];
        const profile = profileDoc.data();

        if (profile.status !== 'active') {
          await signOut(auth);
          clearState();
          setAuthError('Your account is inactive. Contact your administrator.');
          setLoading(false);
          return;
        }

        // Validate role.
        const validRoles = ['AppAdmin', 'AppUser', 'Verifier'];
        if (!validRoles.includes(profile.role)) {
          await signOut(auth);
          clearState();
          setAuthError('Your account has an invalid role. Contact your administrator.');
          setLoading(false);
          return;
        }

        const urlTenantId = sessionStorage.getItem("activeTenantLoginId");

if (urlTenantId && profile.tenantId !== urlTenantId) {
  await signOut(auth);
  clearState();
  setAuthError(
    "This email does not belong to this organization. Please use your organization's login link."
  );
  setLoading(false);
  return;
}

       setCurrentUser(user);
setRole(profile.role);
setTenantId(profile.tenantId || null);
setTenantName(profile.tenantName || null);
setProfileDocId(profileDoc.id);

// Load chatbot feature flag
if (profile.tenantId) {
  try {
    const tenantQuery = query(
      collection(firestore, "Tenants"),
      where("tenantId", "==", profile.tenantId)
    );

    const tenantSnap = await getDocs(tenantQuery);

    if (!tenantSnap.empty) {
      const tenantDoc = tenantSnap.docs[0].data();

      setChatbotEnabled(!!tenantDoc.chatbotEnabled);

      setTenantApplications({
  cbam: tenantDoc.applications?.cbam ?? true,
  energyAudit: tenantDoc.applications?.energyAudit ?? true,
});

      const brandingSnap = await getDoc(
  doc(firestore, "TenantLandingPages", profile.tenantId)
);

if (brandingSnap.exists()) {
  setTenantBranding(brandingSnap.data());
} else {
  setTenantBranding(null);
}
    } else {
      setChatbotEnabled(false);
    }
  } catch (err) {
    console.error("Error loading chatbot feature:", err);
    setChatbotEnabled(false);
  }
} else {
  setChatbotEnabled(false);
}

setLoading(false);
      } catch (err) {
        console.error('AuthContext: error loading profile', err);
        await signOut(auth);
        clearState();
        setAuthError('Could not load your profile. Try again.');
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const value = {
    currentUser,
    role,
    tenantId,
    tenantName,
    profileDocId,
    loading,
    authError,
    logout,
    chatbotEnabled,
    tenantBranding,
    tenantApplications,
    // Convenience flags
    isSuperAdmin: role === 'SuperAdmin',
    isAppAdmin: role === 'AppAdmin',
    isAppUser: role === 'AppUser',
    isVerifier: role === 'Verifier',
    isLoggedIn: !!currentUser && !!role,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
